import { useCallback, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

const LEGACY_RECENT_KEY = 'studio-editor-v2:recent-advertisers'
const RECENT_MAX = 3
const FILE_MAX_COUNT = 3
const FILE_PATTERN = /\.(pdf|jpe?g|png|webp)$/i

function recentKey(userId) {
  return `${LEGACY_RECENT_KEY}:${userId || 'unknown'}`
}

function readRecent(userId) {
  try {
    const raw = window.localStorage.getItem(recentKey(userId))
      // One-time carry-over of the pre-AD-209 list, which was shared by every account.
      ?? window.localStorage.getItem(LEGACY_RECENT_KEY)
      ?? '[]'
    const parsed = JSON.parse(raw)
    return Array.isArray(parsed) ? parsed.filter((item) => item && item.place_id && item.name) : []
  } catch {
    return []
  }
}

function writeRecent(userId, list) {
  try {
    window.localStorage.setItem(recentKey(userId), JSON.stringify(list))
  } catch {
    // Private mode or quota: recents are a convenience only.
  }
}

function normalize(value) {
  const text = typeof value === 'string' ? value.trim() : ''
  return text || null
}

export function advertiserName(advertiser) {
  return advertiser?.name || advertiser?.description || ''
}

/** A website address as typed ("minrath.de", "https://…") → absolute http(s) URL, or null. */
export function websiteAddress(value) {
  const text = String(value || '').trim()
  if (!text) return null
  try {
    const url = new URL(/^[a-z][a-z0-9+.-]*:\/\//i.test(text) ? text : `https://${text}`)
    return ['http:', 'https:'].includes(url.protocol) && url.hostname.includes('.') ? url.toString() : null
  } catch {
    return null
  }
}

/**
 * Shared state of the "Ad Generation with AI" form: Step 1 and the form inside the chat
 * assistant edit the same advertiser, brief, source and files, so switching between them
 * (or coming back from the editor with "Generate Ad with AI") keeps what was entered.
 */
export function useGenerationForm({ userId, language, adId, fileMaxKb = 20480, fileMaxLabel = '20 MB', initial = null }) {
  const { t } = useTranslation()
  const [advertiser, setAdvertiser] = useState(initial?.advertiser || null)
  const [recent, setRecent] = useState(() => (typeof window === 'undefined' ? [] : readRecent(userId)))
  const [clickoutMode, setClickoutMode] = useState('own-website')
  // "Own Website" link target: follows the chosen advertiser's website until the user types one.
  const [clickoutUrl, setClickoutUrlState] = useState(initial?.clickoutUrl || '')
  const clickoutTyped = useRef(false)
  const setClickoutUrl = useCallback((value) => {
    clickoutTyped.current = true
    setClickoutUrlState(value)
  }, [])
  // An ad's saved link put back into the form: not typed here, so another advertiser still replaces it.
  const restoreClickoutUrl = useCallback((value) => {
    clickoutTyped.current = false
    setClickoutUrlState(value)
  }, [])
  const [briefing, setBriefing] = useState('')
  const [sourceKind, setSourceKind] = useState('social')
  const [socialUrl, setSocialUrl] = useState(initial?.socialUrl || '')
  const [websiteUrl, setWebsiteUrl] = useState('')
  const [files, setFiles] = useState([])
  const [fileError, setFileError] = useState('')
  const [attempted, setAttempted] = useState(false)

  const selectAdvertiser = useCallback((place) => {
    if (!place) {
      setAdvertiser(null)
      return
    }
    const entry = {
      place_id: place.place_id,
      name: advertiserName(place) || 'Advertiser',
      address: place.address || place.description || '',
      website: place.website || '',
      phone: place.phone || '',
      category: place.category || '',
      manual: Boolean(place.manual),
    }
    setAdvertiser({ ...place, ...entry })
    if (!clickoutTyped.current) setClickoutUrlState(entry.website)
    setRecent((current) => {
      const next = [entry, ...current.filter((item) => item.place_id !== entry.place_id)].slice(0, RECENT_MAX)
      writeRecent(userId, next)
      return next
    })
  }, [userId])

  const addFiles = useCallback((incoming) => {
    const list = Array.from(incoming || [])
    if (!list.length) return
    const next = [...files, ...list]
    if (next.length > FILE_MAX_COUNT) { setFileError(t('editorV2.generation.files.tooMany', { count: FILE_MAX_COUNT })); return }
    if (list.some((file) => !FILE_PATTERN.test(file.name))) { setFileError(t('editorV2.generation.files.wrongType')); return }
    if (list.some((file) => file.size > fileMaxKb * 1024)) { setFileError(t('editorV2.generation.files.tooLarge', { size: fileMaxLabel })); return }
    setFileError('')
    setFiles(next)
  }, [fileMaxKb, fileMaxLabel, files, t])

  const removeFile = useCallback((index) => {
    setFiles((current) => current.filter((_, position) => position !== index))
    setFileError('')
  }, [])

  const clickoutTarget = clickoutMode === 'own-website' ? websiteAddress(clickoutUrl) : null
  const clickoutUrlError = clickoutMode === 'own-website' && !clickoutTarget
    ? t('editorV2.generation.form.websiteRequired')
    : null
  const canStart = Boolean(advertiser) && !clickoutUrlError

  // The request body of POST /ad-generation/generations: the social post or website override, the brief and
  // every attached file (as the primary source when "PDF or Images" is chosen, otherwise as
  // supporting material). An empty website falls back to the advertiser's listing.
  const sources = useCallback(() => ({
    ad_id: normalize(adId),
    advertiser_name: advertiserName(advertiser) || 'Advertiser',
    google_place_id: advertiser?.manual ? null : normalize(advertiser?.place_id),
    website: (sourceKind === 'website' ? normalize(websiteUrl) : null) || normalize(advertiser?.website),
    language: normalize(language) || 'en',
    briefing_text: normalize(briefing),
    social_post_url: sourceKind === 'social' ? normalize(socialUrl) : null,
    destination_url: clickoutTarget,
    source_files: files,
  }), [adId, advertiser, briefing, clickoutTarget, files, language, socialUrl, sourceKind, websiteUrl])

  // What Campaign Setting stores about the advertiser (editor metadata `campaign.advertiser`).
  const campaignAdvertiser = useMemo(() => (advertiser ? {
    name: advertiserName(advertiser),
    placeId: advertiser.manual ? null : (advertiser.place_id || null),
    address: advertiser.address || '',
    website: advertiser.website || '',
    phone: advertiser.phone || '',
    category: advertiser.category || '',
    manual: Boolean(advertiser.manual),
  } : null), [advertiser])

  return {
    advertiser,
    selectAdvertiser,
    recent,
    clickoutMode,
    setClickoutMode,
    clickoutUrl,
    setClickoutUrl,
    restoreClickoutUrl,
    clickoutTarget,
    clickoutUrlError,
    briefing,
    setBriefing,
    sourceKind,
    setSourceKind,
    socialUrl,
    setSocialUrl,
    websiteUrl,
    setWebsiteUrl,
    files,
    addFiles,
    removeFile,
    fileError,
    fileMaxLabel,
    canStart,
    attempted,
    setAttempted,
    sources,
    campaignAdvertiser,
  }
}
